import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import type { FetchResult, MutationFunctionOptions, MutationResult } from '@apollo/client'
import { useMutation as useMutationCore } from '@apollo/client'
import { deleteKeys, requiredCheck } from '@ztechapps/common.utils'
import type { DocumentNode } from '@apollo/client/core'
import type { MutationHookOptions } from '@apollo/client/react/types/types'
import omit from 'lodash/omit'
import type { MaybeMasked } from '@apollo/client/masking'
import type { FilterQuery } from '@ztechapps/common.contract'

export type UseMutationProps = {
  query: DocumentNode
  options?: {
    mutationOptions?: MutationHookOptions<any, any, any, any>
  }
}
export type MutationInput = Record<string, any>
export type MutationFunctionArgs<TArgs = MutationInput> = { _id?: string; filter?: Record<string, any>; input?: TArgs }
export type MutationFunctionResult<TResult = any> = Promise<FetchResult<MaybeMasked<TResult>>> //Promise<FetchResult<TResult>>
export type MutationFunction<TResult = MutationInput, TArgs = TResult> = (args: MutationFunctionArgs<TArgs>) => MutationFunctionResult<TResult>
export type UseMutationResult<TResult = MutationInput, TArgs = MutationInput> = [
  MutationFunction<TResult, TArgs>,
  //(options?: MutationFunctionOptions<any, any, any, any>) => MutationFunctionResult<TResult>,
  mutate: (options?: MutationFunctionOptions<TResult, TArgs, any, any>) => Promise<FetchResult<MaybeMasked<TResult>>>,
  MutationResult<TResult>
]

export type UseLeanMutationResult<TResult = MutationInput, TArgs = TResult> = [
  (args: TArgs) => Promise<FetchResult<TResult>>,
  MutationResult<TResult>
]

export type UseActionResult<TResult = MutationInput, TArgs = TResult> = [
  (args?: TArgs, filter?: FilterQuery<TResult>, _id?: string) => Promise<TResult>,
  { error: any; loading: boolean; canExecute?: boolean }
]
type Variables = {
  filter?: Record<string, any>
  input?: Record<string, any>
}
export function useMutation<TResult = MutationInput, TInput extends object = MutationInput>({
  query,
  options = {},
}: UseMutationProps): UseMutationResult<TResult, TInput> {
  requiredCheck(query, 'useMutation.query')

  //setup the variables
  const { mutationOptions = {} } = options

  //just return the result of this operation
  const [mutate, mutationResult] = useMutationCore<TResult, Variables>(query, mutationOptions)

  const executeIt: MutationFunction<TResult, TInput> = ({ _id, filter, input }) => {
    //prepare for the variable filter
    //no need to throw error both  filter and _id are undefined
    const inputId = get(input, '_id')
    if (_id === undefined && filter === undefined && inputId === undefined) {
      _id = null
    }
    //if id is specified override the filter, and filter it by _id
    if (!isEmpty(_id) || _id === null) {
      filter = { _id }
    } else if (!isEmpty(inputId) || inputId === null) {
      filter = { _id: inputId }
    }

    //set the doc as the input
    let doc: Partial<TInput> = input ?? {}

    //TODO: This is just to handle the case of input._id === 'any'
    //So no breaking changes will be made
    if (inputId === 'any' && isEmpty(input)) {
      //delete input._id
      doc = omit(input, ['_id'])
    }

    //we need to cleanup the doc and remove the __typename
    doc = deleteKeys(doc, ['@@id', '__typename'])

    //extract the options that can passed to mutate
    const { optimisticResponse, refetchQueries, update } = mutationOptions || {}

    //return the mutate promise
    return mutate({
      optimisticResponse,
      refetchQueries,
      update,
      variables: {
        filter: filter,
        input: doc,
      },
    })
  }

  return [executeIt, mutate, mutationResult]
}

export function useActionMutation<TResult = MutationInput, TInput extends object = MutationInput>(
  props: UseMutationProps
): UseActionResult<TResult, TInput> {
  const [executeIt, _, { error, loading }] = useMutation<TResult, TInput>(props)

  const onExecute = async (input: TInput, filter?: FilterQuery<TResult>, _id?: string) => {
    const result = await executeIt({
      input,
      filter,
      _id,
    })

    //we need to get the first property of  result?.data?
    return result?.data ? (Object.values(result.data)[0] as TResult) : undefined
  }

  return [onExecute, { error, loading }]
}
